import React, { useState, useEffect } from 'react';
import { useWeb3React } from '@web3-react/core';
import { ethers } from 'ethers';
import { 
  getPartyAddresses, 
  getTokenAddresses
} from '../utils/contract';
import { contractAddress } from '../contracts/contractABI';

// ERC20 ABI for approval function
const ERC20_ABI = [
  {
    "constant": false,
    "inputs": [
      {
        "name": "spender",
        "type": "address"
      },
      {
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "approve",
    "outputs": [
      {
        "name": "",
        "type": "bool"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [
      {
        "name": "owner",
        "type": "address"
      },
      {
        "name": "spender",
        "type": "address"
      }
    ],
    "name": "allowance",
    "outputs": [
      {
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  }
];

const TokenApproval = () => {
  const { active, account, library } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [txResult, setTxResult] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [tokenAddresses, setTokenAddresses] = useState({ nilaToken: null, usdcToken: null });
  const [nilaAllowance, setNilaAllowance] = useState('0');
  const [usdcAllowance, setUsdcAllowance] = useState('0');
  const [approvalAmount, setApprovalAmount] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);

  // Determine user role and get token addresses
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partyAddresses, tokens] = await Promise.all([
          getPartyAddresses(),
          getTokenAddresses()
        ]);
        
        setTokenAddresses(tokens);
        
        if (active && account) {
          const accountLower = account.toLowerCase();
          if (accountLower === partyAddresses.partyN?.toLowerCase()) {
            setUserRole('N');
          } else if (accountLower === partyAddresses.partyB?.toLowerCase()) {
            setUserRole('B');
          } else if (accountLower === partyAddresses.partyE?.toLowerCase()) {
            setUserRole('E');
          } else {
            setUserRole(null);
          }
        } else {
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, [active, account]);

  // Check token allowances
  useEffect(() => {
    const checkAllowances = async () => {
      if (!active || !account || !tokenAddresses.nilaToken || !tokenAddresses.usdcToken) {
        return;
      }
      
      try {
        // Create token contract instances
        const nilaContract = new ethers.Contract(
          tokenAddresses.nilaToken,
          ERC20_ABI,
          library
        );
        
        const usdcContract = new ethers.Contract(
          tokenAddresses.usdcToken,
          ERC20_ABI,
          library
        );
        
        // Get allowances
        const [nilaAllowanceWei, usdcAllowanceWei] = await Promise.all([
          nilaContract.allowance(account, contractAddress),
          usdcContract.allowance(account, contractAddress)
        ]);
        
        // Format allowances
        setNilaAllowance(ethers.formatUnits(nilaAllowanceWei, 18)); // Assuming 18 decimals for NILA
        setUsdcAllowance(ethers.formatUnits(usdcAllowanceWei, 6)); // USDC has 6 decimals
      } catch (error) {
        console.error('Error checking allowances:', error);
      }
    };
    
    checkAllowances();
  }, [active, account, tokenAddresses, library]);

  const handleAmountChange = (e) => {
    // Only allow numbers and decimals
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setApprovalAmount(value);
    }
  };

  const handleTokenSelect = (token) => {
    setSelectedToken(token);
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    
    if (!active || !account || !selectedToken || !approvalAmount || parseFloat(approvalAmount) <= 0) {
      return;
    }
    
    setLoading(true);
    setTxResult(null);
    
    try {
      const tokenAddress = selectedToken === 'NILA' 
        ? tokenAddresses.nilaToken 
        : tokenAddresses.usdcToken;
      
      const decimals = selectedToken === 'NILA' ? 18 : 6;
      
      // Create token contract instance with signer
      const signer = await library.getSigner(account);
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ERC20_ABI,
        signer
      );
      
      // Convert amount to wei
      const amountWei = ethers.parseUnits(approvalAmount, decimals);
      
      // Send approval transaction
      const tx = await tokenContract.approve(contractAddress, amountWei);
      const receipt = await tx.wait();
      
      setTxResult({
        success: true,
        message: `Approved ${approvalAmount} ${selectedToken} for contract use`,
        hash: receipt.hash
      });
      
      // Update allowances
      if (selectedToken === 'NILA') {
        setNilaAllowance(approvalAmount);
      } else {
        setUsdcAllowance(approvalAmount);
      }
      
      setApprovalAmount('');
      setSelectedToken(null);
    } catch (error) {
      console.error('Error approving tokens:', error);
      setTxResult({
        success: false,
        message: `Error: ${error.message || 'Transaction failed'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMaxAmount = () => {
    if (selectedToken === 'NILA') {
      setApprovalAmount('100000000'); // 100 million NILA
    } else {
      setApprovalAmount('10000000'); // 10 million USDC
    }
  };

  if (!active || !account) {
    return (
      <div className="token-approval">
        <h2>Token Approvals</h2>
        <p>Please connect your wallet to approve tokens.</p>
      </div>
    );
  }

  return (
    <div className="token-approval">
      <h2>Token Approvals</h2>
      
      <div className="info-note">
        <p>
          <strong>Note:</strong> Before depositing, you need to approve the contract to spend your tokens.
        </p>
        <p>
          <strong>Current Allowances:</strong>
        </p>
        <ul>
          <li>NILA: {nilaAllowance} tokens</li>
          <li>USDC: {usdcAllowance} tokens</li>
        </ul>
      </div>
      
      {!selectedToken ? (
        <div className="token-options">
          <p>Select a token to approve:</p>
          <div className="token-buttons">
            <button 
              onClick={() => handleTokenSelect('NILA')} 
              className="token-button"
            >
              Approve NILA
            </button>
            
            <button 
              onClick={() => handleTokenSelect('USDC')} 
              className="token-button"
            >
              Approve USDC
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleApprove}>
          <h3>Approve {selectedToken}</h3>
          
          <div className="form-group">
            <label htmlFor="approvalAmount">Amount to Approve:</label>
            <div className="input-with-button">
              <input
                id="approvalAmount"
                type="text"
                value={approvalAmount}
                onChange={handleAmountChange}
                placeholder="0.0"
                disabled={loading}
                className="form-control"
              />
              <button 
                type="button" 
                onClick={handleMaxAmount}
                className="max-button"
              >
                MAX
              </button>
            </div>
          </div>
          
          <div className="approval-requirements">
            <p>
              <strong>Recommended Approvals:</strong>
            </p>
            <ul>
              {userRole === 'N' && (
                <li>Party N should approve at least 50,000,000 NILA tokens</li>
              )}
              {userRole === 'B' && (
                <li>Party B should approve sufficient USDC for the loan amount</li>
              )}
              {userRole === 'E' && (
                <>
                  <li>Party E should approve at least 25,000,000 NILA tokens</li>
                  <li>Party E should also approve sufficient USDC for interest payments</li>
                </>
              )}
            </ul>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => setSelectedToken(null)}
              disabled={loading}
              className="back-button"
            >
              Back
            </button>
            
            <button 
              type="submit" 
              disabled={loading || !approvalAmount || parseFloat(approvalAmount) <= 0}
              className="submit-button"
            >
              {loading ? 'Processing...' : `Approve ${selectedToken}`}
            </button>
          </div>
        </form>
      )}
      
      {txResult && (
        <div className={`transaction-result ${txResult.success ? 'success' : 'error'}`}>
          <p>{txResult.message}</p>
          {txResult.hash && (
            <a 
              href={`https://testnet.bscscan.com/tx/${txResult.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-link"
            >
              View Transaction
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenApproval;
